import React, { useMemo, useLayoutEffect } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  Cell
} from 'recharts';
import { aggregateDailyExercisesReportCharts } from './dailyExercisesReportChartsUtils';

const BAR_FILL = '#6366f1';
const BAR_FILL_ALT = '#8b5cf6';
const BAR_FILL_BUCKETS = ['#6366f1', '#8b5cf6', '#a855f7', '#d946ef', '#94a3b8'];

function bucketLabel(t, key) {
  const map = {
    '0-25': 'chartBucket_0_25',
    '25-50': 'chartBucket_25_50',
    '50-75': 'chartBucket_50_75',
    '75-100': 'chartBucket_75_100',
    '?': 'chartBucket_unknown'
  };
  const k = map[key] || 'chartBucket_unknown';
  return t(`reports.dailyExercisesApi.${k}`);
}

/**
 * ثلاثة مبيانات تفصيلية فوق جدول التقرير — البيانات من كامل مجموعة الصفوف الممررة (نفس نطاق التصدير).
 */
const CHART_H = 280;

const DailyExercisesReportCharts = ({ rows, loading, partial, formatLevelName, t }) => {
  useLayoutEffect(() => {
    const id = window.requestAnimationFrame(() => {
      window.dispatchEvent(new Event('resize'));
    });
    return () => window.cancelAnimationFrame(id);
  }, [rows?.length]);

  const { byLevel, byDept, byBucket } = useMemo(
    () => aggregateDailyExercisesReportCharts(rows || [], formatLevelName),
    [rows, formatLevelName]
  );

  const bucketChartData = useMemo(
    () =>
      (byBucket || []).map((b) => ({
        ...b,
        label: bucketLabel(t, b.key)
      })),
    [byBucket, t]
  );

  if (loading) {
    return (
      <div className="mb-6 rounded-xl border border-slate-200/90 bg-slate-50/80 px-4 py-10 text-center text-sm font-semibold text-slate-600">
        {t('reports.dailyExercisesApi.chartsLoading')}
      </div>
    );
  }

  if (!rows || rows.length === 0) {
    return (
      <div className="mb-6 rounded-xl border border-dashed border-slate-200 bg-slate-50/50 px-4 py-8 text-center text-sm text-slate-500">
        {t('reports.dailyExercisesApi.chartsEmpty')}
      </div>
    );
  }

  const chartBox = 'rounded-xl border border-slate-200/90 bg-white p-3 shadow-sm shadow-slate-900/[0.04]';
  const title = 'text-sm font-bold text-slate-800 mb-2';

  return (
    <div className="mb-6 space-y-4">
      <div>
        <h2 className="text-base font-bold text-slate-900">{t('reports.dailyExercisesApi.chartsSectionTitle')}</h2>
        <p className="mt-1 text-xs font-medium text-slate-500">{t('reports.dailyExercisesApi.chartsFootnote')}</p>
        {partial ? (
          <p className="mt-1 text-xs font-semibold text-amber-800/90">{t('reports.dailyExercisesApi.chartsPartialNote')}</p>
        ) : null}
      </div>
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <div className={chartBox}>
          <h3 className={title}>{t('reports.dailyExercisesApi.chartByLevelTitle')}</h3>
          <div className="w-full min-w-0" style={{ height: CHART_H }} dir="ltr">
            <ResponsiveContainer width="100%" height={CHART_H}>
              <BarChart layout="vertical" data={byLevel} margin={{ top: 4, right: 12, left: 4, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-slate-200" />
                <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                <YAxis
                  type="category"
                  dataKey="name"
                  width={88}
                  tick={{ fontSize: 10 }}
                  interval={0}
                />
                <Tooltip
                  contentStyle={{ fontSize: 12 }}
                  formatter={(v) => [v, t('reports.dailyExercisesApi.chartStudentCount')]}
                />
                <Bar
                  dataKey="count"
                  name={t('reports.dailyExercisesApi.chartStudentCount')}
                  fill={BAR_FILL}
                  radius={[0, 4, 4, 0]}
                  isAnimationActive={false}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className={chartBox}>
          <h3 className={title}>{t('reports.dailyExercisesApi.chartByDepartmentTitle')}</h3>
          <div className="w-full min-w-0" style={{ height: CHART_H }} dir="ltr">
            <ResponsiveContainer width="100%" height={CHART_H}>
              <BarChart layout="vertical" data={byDept} margin={{ top: 4, right: 12, left: 4, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-slate-200" />
                <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                <YAxis type="category" dataKey="name" width={88} tick={{ fontSize: 10 }} interval={0} />
                <Tooltip
                  contentStyle={{ fontSize: 12 }}
                  formatter={(value, _name, props) => {
                    const dk = props?.dataKey ?? props?.payload?.dataKey;
                    if (dk === 'totalSessions') return [value, t('reports.dailyExercisesApi.chartTotalSessions')];
                    return [value, t('reports.dailyExercisesApi.chartStudentCount')];
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar
                  dataKey="totalSessions"
                  name={t('reports.dailyExercisesApi.chartTotalSessions')}
                  fill={BAR_FILL}
                  radius={[0, 4, 4, 0]}
                  isAnimationActive={false}
                />
                <Bar
                  dataKey="students"
                  name={t('reports.dailyExercisesApi.chartStudentCount')}
                  fill={BAR_FILL_ALT}
                  radius={[0, 4, 4, 0]}
                  isAnimationActive={false}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className={chartBox}>
          <h3 className={title}>{t('reports.dailyExercisesApi.chartAvgBucketsTitle')}</h3>
          <div className="w-full min-w-0" style={{ height: CHART_H }} dir="ltr">
            <ResponsiveContainer width="100%" height={CHART_H}>
              <BarChart data={bucketChartData} margin={{ top: 8, right: 12, left: 4, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-slate-200" />
                <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={0} angle={-18} textAnchor="end" height={68} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip
                  contentStyle={{ fontSize: 12 }}
                  formatter={(v) => [v, t('reports.dailyExercisesApi.chartStudentCount')]}
                />
                <Bar dataKey="count" name={t('reports.dailyExercisesApi.chartStudentCount')} radius={[4, 4, 0, 0]} isAnimationActive={false}>
                  {bucketChartData.map((entry, index) => (
                    <Cell key={`cell-${entry.key}`} fill={BAR_FILL_BUCKETS[index % BAR_FILL_BUCKETS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DailyExercisesReportCharts;
